import { Injectable } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

// servicios
import { UsuarioService } from './datos/usuario.service';
import { NotificacionesService } from './notificaciones.service';

// librerías y entronos 
import Swal from 'sweetalert2';
import { IUsuario } from '../interfaces/usuario';

// Servicio reutilizable para cambiar contraseña
@Injectable({
  providedIn: 'root'
})
export class CambiarPasswordService {

  constructor(
    private fb: FormBuilder,
    private usuarioSrv: UsuarioService,
    private notificacionesSrv: NotificacionesService
  ) { }

  // Validador personalizado para confirmar contraseña
  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  // Método principal para mostrar el modal de cambio de contraseña
  async showChangePasswordModal(usuarioio?: IUsuario): Promise<boolean> {
    const form = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(4)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    const { value: formValues } = await Swal.fire({
      title: 'Cambiar Contraseña',
      html: `
        <div class="change-password-form">
          <div class="form-group mb-1">
          <span class="form-label p-2 mb-3">
            <i class="fa-solid fa-user"></i> <strong>${usuarioio?.username || ''}</strong>
          </span><br>
          <label for="newPassword" class="form-label">Nueva Contraseña</label>
            <input 
              type="password" 
              id="newPassword" 
              class="swal2-input form-control"  
              placeholder="Nueva contraseña" 
              autocomplete="new-password"
              style="margin: 5px 0;"
            >
            <div id="newPasswordError" class="text-danger small" style="display: none;"></div>
          </div>
          
          <div class="form-group mb-3">
            <label for="confirmPassword" class="form-label">Confirmar Contraseña</label>
            <input 
              type="password" 
              id="confirmPassword" 
              class="swal2-input form-control" 
              placeholder="Confirma nueva contraseña"
              autocomplete="new-password"
              style="margin: 5px 0;"
            >
            <div id="confirmPasswordError" class="text-danger small" style="display: none;"></div>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Cambiar Contraseña',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#dc3545',
      width: '500px',
      customClass: {
        container: 'change-password-modal'
      },
      preConfirm: () => {
        const newPassword = (document.getElementById('newPassword') as HTMLInputElement).value;
        const confirmPassword = (document.getElementById('confirmPassword') as HTMLInputElement).value;

        // Limpiar errores previos
        this.clearErrors();

        let hasErrors = false;

        // Validar nueva contraseña
        if (!newPassword) {
          this.showFieldError('newPasswordError', 'La nueva contraseña es requerida');
          hasErrors = true;
        } else if (newPassword.length < 4) {
          this.showFieldError('newPasswordError', 'La contraseña debe tener al menos 4 caracteres');
          hasErrors = true;
        }

        // Validar confirmación de contraseña
        if (!confirmPassword) {
          this.showFieldError('confirmPasswordError', 'Debes confirmar la nueva contraseña');
          hasErrors = true;
        } else if (newPassword !== confirmPassword) {
          this.showFieldError('confirmPasswordError', 'Las contraseñas no coinciden');
          hasErrors = true;
        }

        if (hasErrors) {
          return false;
        }

        return {
          newPassword: newPassword,
          confirmPassword: confirmPassword
        };
      },
      didOpen: () => {
        // Agregar event listeners para validación en tiempo real
        const newPasswordInput = document.getElementById('newPassword') as HTMLInputElement;
        const confirmPasswordInput = document.getElementById('confirmPassword') as HTMLInputElement;

        newPasswordInput.addEventListener('input', () => {
          this.validatePasswordField(newPasswordInput.value, 'newPasswordError');
        });

        confirmPasswordInput.addEventListener('input', () => {
          this.validateConfirmPasswordField(
            newPasswordInput.value,
            confirmPasswordInput.value,
            'confirmPasswordError'
          );
        });
      }
    });

    if (formValues) {
      return await this.updatePassword({
        id: usuarioio!.id,
        password: formValues.newPassword,
        password2: formValues.newPassword,
      });
    }

    return false;
  }

  // Método para actualizar la contraseña
  private async updatePassword(data: any): Promise<boolean> {
    try {
      // Mostrar loading
      Swal.fire({
        title: 'Actualizando contraseña...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      return new Promise((resolve) => {
        this.usuarioSrv.userUpdatePsw(data)
          .subscribe((resp: any) => {
            Swal.close();

            if (resp.error) {
              this.notificacionesSrv.aviso('error', resp.mensaje);

              // Mostrar errores específicos del API
              if (typeof resp.mensaje === 'object') {
                let errorMessages = '';
                for (let controlName in resp.mensaje) {
                  errorMessages += `${controlName}: ${resp.mensaje[controlName]}\n`;
                }

                Swal.fire({
                  icon: 'error',
                  title: 'Error al cambiar contraseña',
                  text: errorMessages,
                  confirmButtonColor: '#dc3545'
                });
              }

              resolve(false);
              return;
            }

            // Éxito
            Swal.fire({
              icon: 'success',
              title: 'Contraseña actualizada',
              text: 'La contraseña se ha cambiado exitosamente',
              confirmButtonColor: '#28a745'
            });

            resolve(true);
          }, (error: any) => {
            Swal.close();

            Swal.fire({
              icon: 'error',
              title: 'Error de conexión',
              text: 'No se pudo conectar con el servidor. Intenta nuevamente.',
              confirmButtonColor: '#dc3545'
            });

            resolve(false);
          });
      });

    } catch (error) {
      Swal.close();
      console.error('Error al cambiar contraseña:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error inesperado',
        text: 'Ocurrió un error inesperado. Intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });

      return false;
    }
  }

  // Métodos auxiliares para manejo de errores
  private showFieldError(elementId: string, message: string): void {
    const errorElement = document.getElementById(elementId);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
  }

  private clearErrors(): void {
    const errorElements = ['newPasswordError', 'confirmPasswordError'];
    errorElements.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.style.display = 'none';
        element.textContent = '';
      }
    });
  }

  private validatePasswordField(password: string, errorElementId: string): void {
    if (!password) {
      this.showFieldError(errorElementId, 'La contraseña es requerida');
    } else if (password.length < 4) {
      this.showFieldError(errorElementId, 'La contraseña debe tener al menos 4 caracteres');
    } else {
      const errorElement = document.getElementById(errorElementId);
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }
  }

  private validateConfirmPasswordField(password: string, confirmPassword: string, errorElementId: string): void {
    if (!confirmPassword) {
      this.showFieldError(errorElementId, 'Debe confirmar la contraseña');
    } else if (password !== confirmPassword) {
      this.showFieldError(errorElementId, 'Las contraseñas no coinciden');
    } else {
      const errorElement = document.getElementById(errorElementId);
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }
  }
}
